import React from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'

import {
  HOVER_DIMMED_OPACITY,
  HOVER_FOCUSED_OPACITY,
  HOVER_TRANSITION_CSS,
} from 'features/charts-v2/chart-hover-constants'
import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
// bully ignore[shared-imports-features: rule's applies predicate is always_ts_file (tools/bully/policy/src/rules/rules/shared_imports_features/mod.rs:19), not scoped to configured shared_layer_paths — this file already imports several charts-v2 sibling modules on adjacent lines. follow-up: .agents/follow-ups/bully-typescript-false-positive-rules.md]
import { DEFAULT_SERIES_COLOR } from 'features/charts-v2/services/series-color-palette'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import {
  calculateAvailableXAxisWidth,
  calculateXAxisHeightFromLabels,
  formatAxisNumber,
  OVERFLOW_CONFIG,
} from 'features/charts-v2/utils/axis-tick-utils'
import { CustomXAxisTick } from 'features/charts-v2/utils/AxisTickUtils'
import {
  companionVolumeRow,
  countMetricRow,
  metricRow,
} from 'features/charts-v2/utils/build-hover-annotation'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;

  /* Dim the area fill when any dot is hovered */
  &:has(.recharts-area-dots circle:hover) .recharts-area-area {
    opacity: ${HOVER_DIMMED_OPACITY};
    transition: ${HOVER_TRANSITION_CSS};
  }

  /* Dim non-hovered dots */
  &:has(.recharts-area-dots circle:hover) .recharts-area-dots circle {
    opacity: ${HOVER_DIMMED_OPACITY};
    transition: ${HOVER_TRANSITION_CSS};
  }

  /* Keep the hovered dot bright */
  &:has(.recharts-area-dots circle:hover) .recharts-area-dots circle:hover {
    opacity: ${HOVER_FOCUSED_OPACITY} !important;
  }
`

/**
 * Format number for display in labels
 * - Whole numbers: no decimals (e.g., 5)
 * - Decimals: max 2 decimal places (e.g., 4.91, 3.5)
 */
const formatLabelValue = (value: number): string => {
  if (Number.isInteger(value)) {
    return value.toString()
  }
  // Round to 2 decimal places and remove trailing zeros
  return parseFloat(value.toFixed(2)).toString()
}

/**
 * Custom label component for area data points
 * Displays the value above each data point
 */
const CustomAreaLabel = React.memo<{
  x?: number
  y?: number
  value?: number
  fontSizePx?: number
}>(({ x = 0, y = 0, value, fontSizePx = 13 }) => {
  // Don't render label if value is null/undefined/0
  if (value == null || value === 0) return null

  return (
    <text
      x={x}
      y={y - 10}
      fill={DEFAULT_SERIES_COLOR}
      fontSize={fontSizePx}
      textAnchor="middle"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {formatLabelValue(value)}
    </text>
  )
})

CustomAreaLabel.displayName = 'CustomAreaLabel'

// Recharts area chart event types
interface RechartsAreaMouseEvent {
  activeLabel?: string
}

interface AreaClickData {
  payload?: { name: string; count: number }
}

interface DotProps {
  cx: number
  cy: number
  payload?: { name: string }
}

export class AreaChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.AREA
  public readonly requiredMetrics: MetricType[] = []
  public readonly optionalMetrics: MetricType[] = []

  public transform(props: ChartProps): TransformedChartData {
    return dataTransformationService.transformForBasicChart(
      props.data!,
      props.selectedMetrics,
      props.timeBucket,
    )
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      transformedData,
      onDataPointHover,
      selectedMetrics,
      onSegmentClick,
      fontSizePx = 13,
      showXAxisLabel = true,
      showYAxisLabel = true,
      showDatapointLabels = true,
      xAxisOverflow = 'expand',
      yAxisDomain,
      disableAnimation = false,
    } = props

    if (!transformedData.chartData || transformedData.chartData.length === 0) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    const primaryMetric = selectedMetrics[0]!
    const isVolumeMetric =
      primaryMetric === 'rating' || primaryMetric === 'sentiment'
    const isCountMetric =
      primaryMetric === 'record_count' || primaryMetric === 'sentence_count'

    let tooltipTotal = 0
    if (isCountMetric) {
      tooltipTotal = transformedData.chartData.reduce(
        (sum, d) => sum + ((d['count'] as number) || 0),
        0,
      )
    } else if (isVolumeMetric) {
      tooltipTotal = transformedData.chartData.reduce(
        (sum, d) => sum + ((d['volumeCount'] as number) || 0),
        0,
      )
    }

    // Calculate dynamic maxWidth for wrap mode based on data length
    // Estimate: assume chart is ~1200px wide (typical for full-width charts)
    const estimatedChartWidth = 1200
    const maxWidthPerLabel =
      xAxisOverflow === 'wrap'
        ? calculateAvailableXAxisWidth(
            estimatedChartWidth,
            transformedData.chartData.length,
            { left: 40, right: 30 },
          )
        : 100

    const tickRenderer = (props: {
      x: number
      y: number
      payload: { value: string }
      [k: string]: unknown
    }): JSX.Element => (
      <CustomXAxisTick
        {...props}
        fontSizePx={fontSizePx}
        overflowMode={xAxisOverflow}
        maxWidth={maxWidthPerLabel}
      />
    )

    // Extract labels from chart data
    const labels = transformedData.chartData.map((d) => String(d['name'] ?? ''))

    // Calculate x-axis height dynamically based on overflow mode
    let xAxisHeight: number
    let bottomMargin: number

    if (xAxisOverflow === 'expand') {
      // Calculate based on actual text width for angled labels
      xAxisHeight = calculateXAxisHeightFromLabels(labels, fontSizePx)
      bottomMargin = 20
    } else {
      // Use fixed configuration for wrap and truncate modes
      const overflowConfig = OVERFLOW_CONFIG[xAxisOverflow]
      xAxisHeight = overflowConfig.height
      bottomMargin = overflowConfig.margin
    }

    return (
      <ChartContainer>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            key={`chart-${yAxisDomain?.[1] ?? 'auto'}`}
            data={transformedData.chartData}
            margin={{ ...this.getCommonMargin(), bottom: bottomMargin }}
            onMouseMove={(data: RechartsAreaMouseEvent) => {
              if (data && data.activeLabel) {
                onDataPointHover?.(data.activeLabel)
              }
            }}
            onMouseLeave={() => onDataPointHover?.(null)}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              height={xAxisHeight}
              interval={0}
              tick={showXAxisLabel ? tickRenderer : false}
            />
            <YAxis
              tick={{ fontSize: fontSizePx }}
              tickFormatter={formatAxisNumber}
              domain={yAxisDomain || undefined}
              label={
                showYAxisLabel
                  ? {
                      value: this.getMetricLabel([selectedMetrics[0]!]),
                      angle: -90,
                      position: 'insideLeft',
                      style: {
                        textAnchor: 'middle',
                        fill: '#666',
                        fontSize: fontSizePx,
                      },
                    }
                  : undefined
              }
            />
            <Tooltip
              content={({
                active,
                payload: tooltipPayload,
                label: tooltipLabel,
              }) => {
                if (!active || !tooltipPayload?.length) return null
                const entry = tooltipPayload[0]
                if (!entry) return null
                const val = entry.value as number
                const dp = entry.payload as Record<string, unknown>

                const rows = []
                if (isVolumeMetric) {
                  rows.push(metricRow(primaryMetric, val))
                  const volumeCount = dp['volumeCount'] as number | undefined
                  const volRow = companionVolumeRow(
                    primaryMetric,
                    volumeCount,
                    tooltipTotal,
                  )
                  if (volRow) rows.push(volRow)
                } else if (isCountMetric) {
                  rows.push(countMetricRow(primaryMetric, val, tooltipTotal))
                } else {
                  rows.push(metricRow(primaryMetric, val))
                }

                return (
                  <ChartDatapointHoverAnnotation
                    title={String(tooltipLabel)}
                    rows={rows}
                    footer={null}
                    focusedField={null}
                  />
                )
              }}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke={DEFAULT_SERIES_COLOR}
              strokeWidth={2}
              fill={DEFAULT_SERIES_COLOR}
              fillOpacity={0.6}
              name={this.getMetricLabel(selectedMetrics)}
              isAnimationActive={!showDatapointLabels && !disableAnimation}
              label={
                showDatapointLabels
                  ? (props: Record<string, unknown>) => (
                      <CustomAreaLabel {...props} fontSizePx={fontSizePx} />
                    )
                  : false
              }
              onClick={
                ((
                  data: AreaClickData,
                  _index?: number,
                  event?: React.MouseEvent,
                ) => {
                  if (onSegmentClick && data && data.payload && event) {
                    onSegmentClick(
                      {
                        primaryBreakdown: data.payload.name,
                        value: data.payload.count || 0,
                        payload: data.payload as unknown as Record<
                          string,
                          string | number | boolean | null
                        >,
                      },
                      event,
                    )
                  }
                }) as never
              }
              dot={(dotProps: DotProps) => (
                <circle
                  cx={dotProps.cx}
                  cy={dotProps.cy}
                  r={4}
                  fill={DEFAULT_SERIES_COLOR}
                  stroke={DEFAULT_SERIES_COLOR}
                  strokeWidth={2}
                />
              )}
              activeDot={{
                r: 6,
                stroke: DEFAULT_SERIES_COLOR,
                strokeWidth: 2,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartContainer>
    )
  }

  public getTitle(): string {
    return 'Posts Trend'
  }
}
